/**
 * Keyword curation for admins (docs/concepts/concept_curated_keywords.md §11.3).
 * The backend enforces the admin role; the checks here only shape the UI.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useAuth } from '../auth';
import { HistoryTab, KeywordsTab, ProposalsTab, ReviewTab, useKeywordVocabulary } from '../components/admin';

type TabId = 'keywords' | 'proposals' | 'review' | 'history';
const TABS: TabId[] = ['keywords', 'proposals', 'review', 'history'];

function KeywordCuration({ accessToken }: { accessToken: string | null }) {
  const { t } = useTranslation('admin');
  const vocabulary = useKeywordVocabulary(accessToken);
  const [tab, setTab] = useState<TabId>('keywords');

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>{t('title')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {vocabulary.active && t('activeRevision', { revision: vocabulary.active.revision })}
      </Typography>

      {vocabulary.problem === 'conflict' && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={<Button color="inherit" onClick={vocabulary.reload}>{t('actions.reload')}</Button>}
        >
          {t('conflict')}
        </Alert>
      )}
      {vocabulary.problem === 'failed' && <Alert severity="error" sx={{ mb: 2 }}>{t('failed')}</Alert>}

      <Tabs value={tab} onChange={(_, value: TabId) => setTab(value)} sx={{ mb: 3 }}>
        {TABS.map((id) => <Tab key={id} value={id} label={t(`tabs.${id}`)} />)}
      </Tabs>

      {/* Tabs mount only once the draft is loaded, so the access token is already set. */}
      {!vocabulary.draft ? (
        vocabulary.problem ? null : <CircularProgress />
      ) : (
        <>
          {tab === 'keywords' && <KeywordsTab vocabulary={vocabulary} />}
          {tab === 'proposals' && <ProposalsTab onDraftChanged={vocabulary.reload} />}
          {tab === 'review' && <ReviewTab vocabulary={vocabulary} onCommitted={() => setTab('history')} />}
          {tab === 'history' && <HistoryTab />}
        </>
      )}
    </Container>
  );
}

export function AdminKeywordsPage() {
  const { t } = useTranslation('admin');
  const { isLoading, isAuthenticated, isAdmin, accessToken, login } = useAuth();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!isAuthenticated) {
    return (
      <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
        <Typography sx={{ mb: 2 }}>{t('signInRequired')}</Typography>
        <Button variant="contained" onClick={login}>{t('common:user.signIn')}</Button>
      </Container>
    );
  }
  if (!isAdmin) return <Navigate to="/" replace />;
  return <KeywordCuration accessToken={accessToken} />;
}
